import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { PageHeader, PageBody, EmptyState } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

export default function Employees() {
  const { t } = useI18n();
  const { data: emps = [] } = useSWR("/employees", fetcher);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", role: "Roofer", phone: "", email: "", hourly_rate: 35, active: true });

  const submit = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      await api.post("/employees", { ...form, hourly_rate: Number(form.hourly_rate) });
      toast.success("Saved");
      setOpen(false);
      setForm({ name: "", role: "Roofer", phone: "", email: "", hourly_rate: 35, active: true });
      mutate("/employees");
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    await api.delete(`/employees/${id}`);
    mutate("/employees");
  };

  const toggleActive = async (e) => {
    await api.patch(`/employees/${e.id}`, { active: !e.active });
    mutate("/employees");
  };

  return (
    <div data-testid="employees-page">
      <PageHeader
        eyebrow="// 09 / Crew"
        title={t("employees.title")}
        sub={t("employees.sub")}
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-employee-button" className="rounded-none bg-zinc-900 hover:bg-orange-600 text-white uppercase font-bold tracking-wider text-xs h-11 px-5">
                <Plus size={14} className="mr-2" weight="bold" /> {t("employees.new")}
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-zinc-300 max-w-md">
              <DialogHeader><DialogTitle className="font-display font-black text-2xl tracking-tight">{t("employees.new")}</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-4">
                <Field label={t("common.name")}><Input data-testid="emp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" /></Field>
                <Field label={t("employees.role")}><Input data-testid="emp-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="rounded-none" /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label={t("common.phone")}><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none" /></Field>
                  <Field label={t("common.email")}><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none" /></Field>
                </div>
                <Field label={t("employees.rate")}><Input data-testid="emp-rate" type="number" step="0.25" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} className="rounded-none mono" /></Field>
                <Button data-testid="emp-save" onClick={submit} className="rounded-none bg-orange-600 hover:bg-orange-700 text-white uppercase font-bold tracking-wider w-full h-11">{t("common.save")}</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />
      <PageBody>
        {emps.length === 0 ? (
          <EmptyState title={t("employees.empty")} sub={t("employees.sub")} />
        ) : (
          <div className="border border-zinc-200">
            <div className="grid grid-cols-12 px-5 py-3 border-b border-zinc-200 label-eyebrow text-zinc-500 bg-zinc-50">
              <div className="col-span-3">{t("common.name")}</div>
              <div className="col-span-2">{t("employees.role")}</div>
              <div className="col-span-3">{t("common.phone")} / {t("common.email")}</div>
              <div className="col-span-2 text-right">{t("employees.rate")}</div>
              <div className="col-span-1">{t("employees.active")}</div>
              <div className="col-span-1"></div>
            </div>
            {emps.map(e => (
              <div key={e.id} data-testid={`emp-row-${e.id}`} className="grid grid-cols-12 px-5 py-3 border-b border-zinc-100 items-center text-sm">
                <div className="col-span-3 font-semibold">{e.name}</div>
                <div className="col-span-2 text-zinc-700">{e.role}</div>
                <div className="col-span-3 mono text-xs text-zinc-600 truncate">{e.phone} {e.email && `· ${e.email}`}</div>
                <div className="col-span-2 text-right mono">${e.hourly_rate.toFixed(2)}/h</div>
                <div className="col-span-1"><Switch data-testid={`emp-active-${e.id}`} checked={e.active} onCheckedChange={() => toggleActive(e)} /></div>
                <div className="col-span-1 text-right">
                  <button data-testid={`emp-delete-${e.id}`} onClick={() => remove(e.id)} className="text-zinc-400 hover:text-orange-600"><Trash size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </PageBody>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-eyebrow text-zinc-600 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
